import { ChangeDetectionStrategy, Component, inject, OnInit, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { catchError, EMPTY } from 'rxjs';
import { ReservationSummaryCommissionEditDialogApiService } from '@/reservation/reservation-summary/reservation-summary-commission-edit-dialog/reservation-summary-commission-edit-dialog.api.service';
import { PricePipe } from '@/common/pipes/price.pipe';
import { toFixedInt } from '@/_utils/to-fixed-int';

interface DialogData {
	reservationId: number;
	currentCommissionValue: number;
	reservationTotalPrice: number;
}

@Component({
	selector: 'app-reservation-summary-commission-edit-dialog',
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		ReactiveFormsModule,
		PricePipe,
	],
	templateUrl: './reservation-summary-commission-edit-dialog.component.html',
	styleUrls: ['./reservation-summary-commission-edit-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationSummaryCommissionEditDialogComponent implements OnInit {
	readonly MIN_COMMISSION_VALUE = 0;
	maxCommissionValue: number;
	newCommission = new FormControl(0, []);

	readonly dialogData: Signal<DialogData> = signal(inject(MAT_DIALOG_DATA));
	private readonly apiService = inject(ReservationSummaryCommissionEditDialogApiService);
	private readonly infobox = inject(InfoboxService);
	private readonly dialogRef = inject(MatDialogRef<ReservationSummaryCommissionEditDialogComponent>);

	ngOnInit(): void {
		this.maxCommissionValue = this.dialogData().reservationTotalPrice;
		this.newCommission.setValidators([
			Validators.required,
			Validators.min(this.MIN_COMMISSION_VALUE),
			Validators.max(this.maxCommissionValue / 100),
		]);
	}

	save(): void {
		this.newCommission.markAsTouched();
		if (this.newCommission.invalid) {
			return;
		}

		this.saveChanges(toFixedInt(this.newCommission.value, 2));
	}

	close(): void {
		this.dialogRef.close();
	}

	private saveChanges(commissionValue: number): void {
		this.apiService
			.updateCommissionValue(this.dialogData().reservationId, commissionValue)
			.pipe(
				catchError(() => {
					this.close();
					return EMPTY;
				}),
			)
			.subscribe(() => {
				this.infobox.success('Pomyślnie zapisano zmiany');
				this.close();
			});
	}
}
